import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import { updatePhoneNumber } from "../../../api/mutation";
import TextInput from "../../../components/InputFields/TextInput";
import { V5, V10, V24 } from "../../../components/InputFields/validator/validators";
import { ModelState } from "../../../interfaces/modelState";
import { force } from "../../../typeguards/force";
import { MutationLayout } from "../MutationLayout";
import { phoneNumberMutationSitecoreLabels } from "./PhoneNumberMutation.labels";
import { initialState, phoneNumberMutationReducer } from "./PhoneNumberMutation.state";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
  policyNr: string;
}

export const PhoneNumberMutation = ({ fields, policyNr }: Props) => {
  const history = useHistory();

  const [{ data, loading, modelState }, dispatch] = useReducer(phoneNumberMutationReducer, initialState);

  // create a copy of the object, because the fields try to mutate it
  const copyOfModelState = { ...modelState };

  const handleUpdateOldPhoneNumber = (oldPhoneNumber: string) => {
    dispatch({ type: "UPDATE_PHONE_NUMBER_MUTATION_DATA", payload: { oldPhoneNumber } });
  };

  const handleUpdateNewPhoneNumber = (newPhoneNumber: string) => {
    dispatch({ type: "UPDATE_PHONE_NUMBER_MUTATION_DATA", payload: { newPhoneNumber } });
  };

  const setModelState = (newModelState: ModelState) => {
    dispatch({ type: "UPDATE_PHONE_NUMBER_MUTATION_ERROR", payload: newModelState });
  };

  const handlePhoneNumberMutationClick = async () => {
    try {
      dispatch({ type: "UPDATE_PHONE_NUMBER_MUTATION_FETCH_START" });
      const request = {
        newPhoneNumber: force(data.newPhoneNumber),
        oldPhoneNumber: data.oldPhoneNumber,
      };
      const response = await updatePhoneNumber({ ...request, policyNumber: policyNr });

      if (response.modelState) {
        dispatch({
          type: "UPDATE_PHONE_NUMBER_MUTATION_FETCH_ERROR",
          payload: response.modelState,
        });
      } else {
        // remove the query parameters
        window.history.replaceState(null, "", window.location.pathname);
        history.push(fields.ThankYouPage.url);
      }
    } catch {
      dispatch({
        type: "UPDATE_PHONE_NUMBER_MUTATION_FETCH_ERROR",
        payload: { global: ["Er is een onverwachte fout opgetreden. Probeer het opnieuw."] },
      });
    }
  };

  const canSubmitForm = Boolean(data.newPhoneNumber);

  return (
    <MutationLayout
      canSubmitForm={canSubmitForm}
      handleSubmit={handlePhoneNumberMutationClick}
      loading={loading}
      modelState={copyOfModelState}
      title="Telefoonnummer wijzigen"
      CardContent={
        <>
          <TextInput
            fieldData={phoneNumberMutationSitecoreLabels}
            name="OldPhoneNumber"
            validators={[V10(15), V24]}
            setModelState={setModelState}
            inputValue={data.oldPhoneNumber}
            modelState={copyOfModelState}
            fieldName="OldPhoneNumber"
            propertyName="model.OldPhoneNumber"
            onBlur={handleUpdateOldPhoneNumber}
          />
          <TextInput
            fieldData={phoneNumberMutationSitecoreLabels}
            name="NewPhoneNumber"
            validators={[V5, V10(15), V24]}
            setModelState={setModelState}
            inputValue={data.newPhoneNumber}
            modelState={copyOfModelState}
            fieldName="NewPhoneNumber"
            propertyName="model.NewPhoneNumber"
            onBlur={handleUpdateNewPhoneNumber}
            required
          />
        </>
      }
      InfoContent={
        <>
          <h6>Verwerkingstermijn</h6>
          <p>
            Wijzigingen zijn niet direct zichtbaar in het DELAportaal De verwerkingstermijn bedraagt maximaal 2
            werkdagen. Er wordt <strong>geen</strong> nieuwe polis afgegeven.
          </p>

          <h6>Nieuw telefoonnummer</h6>
          <p>
            Was er nog geen telefoonnummer bij ons bekend? Dan vul je in het veld ‘Huidig telefoonnummer’ niet in. Je
            geeft dan enkel het nieuwe telefoonnummer op in het veld onder ‘Nieuw telefoonnummer’.
          </p>
        </>
      }
    />
  );
};
